import {
  type LiteralType,
  type TemplateDefinition,
} from './template-definition.type';

export const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null;

export const isTemplateDefinition = (v: unknown): v is TemplateDefinition => {
  if (!isRecord(v)) return false;
  const inputSchema = (v as Record<string, unknown>).inputSchema;
  const steps = (v as Record<string, unknown>).steps;
  const initialStepId = (v as Record<string, unknown>).initialStepId;

  return (
    isRecord(inputSchema) &&
    isRecord(steps) &&
    typeof initialStepId === 'string'
  );
};

export const getPropertyKeysFromSchema = (schema: unknown): string[] => {
  if (!schema || typeof schema !== 'object') return [];
  const s = schema as { properties?: Record<string, unknown> };
  return s.properties ? Object.keys(s.properties) : [];
};

export const getRequiredFieldsFromSchema = (schema: unknown): Set<string> => {
  if (!schema || typeof schema !== 'object') return new Set();
  const s = schema as { required?: string[] };
  return new Set(Array.isArray(s.required) ? s.required : []);
};

const inferLiteralTypeFromSchema = (node: unknown): LiteralType => {
  if (!isRecord(node)) return 'json';
  const type = (node as Record<string, unknown>).type;
  if (typeof type === 'string') {
    if (type === 'string') return 'string';
    if (type === 'number' || type === 'integer') return 'number';
    if (type === 'boolean') return 'boolean';
    if (type === 'null') return 'null';
    return 'json';
  }
  if (Array.isArray(type)) {
    const preferred = type.find((t) => t !== 'null');
    if (preferred === 'string') return 'string';
    if (preferred === 'number' || preferred === 'integer') return 'number';
    if (preferred === 'boolean') return 'boolean';
    if (preferred === 'null') return 'null';
    return 'json';
  }
  const anyOf = (node as Record<string, unknown>).anyOf as
    | unknown[]
    | undefined;
  const oneOf = (node as Record<string, unknown>).oneOf as
    | unknown[]
    | undefined;
  const first = (anyOf && anyOf[0]) || (oneOf && oneOf[0]);
  if (first) return inferLiteralTypeFromSchema(first);
  return 'json';
};

export const getFieldExpectedLiteralTypes = (
  inputSchema: unknown
): Record<string, LiteralType> => {
  const map: Record<string, LiteralType> = {};
  if (isRecord(inputSchema) && isRecord(inputSchema.properties)) {
    const props = inputSchema.properties as Record<string, unknown>;
    for (const key of Object.keys(props)) {
      map[key] = inferLiteralTypeFromSchema(props[key]);
    }
  }
  return map;
};

export const buildEventPathOptions = (schema: unknown): string[] => {
  const paths: string[] = [];
  const visit = (node: unknown, prefix: string) => {
    if (!isRecord(node)) {
      if (prefix) paths.push(prefix);
      return;
    }
    const type = (node as Record<string, unknown>).type;
    if (
      type === 'object' ||
      (isRecord((node as Record<string, unknown>).properties) && !type)
    ) {
      const props = (node as Record<string, unknown>).properties as
        | Record<string, unknown>
        | undefined;
      if (props) {
        for (const [k, child] of Object.entries(props)) {
          visit(child, prefix ? `${prefix}.${k}` : k);
        }
        return;
      }
    }
    if (type === 'array' && 'items' in (node as Record<string, unknown>)) {
      visit(
        (node as Record<string, unknown>).items,
        prefix ? `${prefix}.0` : '0'
      );
      return;
    }
    const anyOf = (node as Record<string, unknown>).anyOf as
      | unknown[]
      | undefined;
    const oneOf = (node as Record<string, unknown>).oneOf as
      | unknown[]
      | undefined;
    if (Array.isArray(anyOf)) anyOf.forEach((n) => visit(n, prefix));
    else if (Array.isArray(oneOf)) oneOf.forEach((n) => visit(n, prefix));
    else if (prefix) paths.push(prefix);
  };
  if (schema) visit(schema, '');
  return Array.from(new Set(paths)).sort();
};
